import { Box, Button } from '@mui/material';
import { Formik, Form } from 'formik';
import dayjs from 'dayjs';
import { DialogUI } from '../../../Interface';
import CreateWorkspaceDialogFields from './CreateWorkspaceDialogFields';
import Yup from '../../../../utils/yup';
import useParam from '../../../../context/ParamContext';
import { useEffect, useState } from 'react';
import useUsers from '../../../../context/UsersContext';
import projectService from '../../../../service/project.service';
import addNotification from '../../../../utils/addNotification';
import { NotificationType } from '../../../../config';
import { cleanValues } from '../../../../utils/helper';
import { useProject } from '../../../../hooks/useProject';

const CreateWorkspaceDialog = ({ open, handleClose, setSelectedProjectId, workspaceState }) => {
  const { autoCompleteData, autoCompleteOptions, setAutoCompleteData } = workspaceState;
  const projectState = useProject();
  const { params } = useParam();
  const { user } = useUsers();

  const defaultValues = {
    ...projectState.def,
    companyContactRole: '',
    companyContactPhoneNumber: '',
    companyContactEmail: '',
  };
  const [initValues, setInitValues] = useState(defaultValues);

  const projectTypeStatusFieldShowed = params.PROJECT_SHOW_PROJECT_TYPE_STATUS === 'Y';

  useEffect(() => {
    if (!open) return;

    setInitValues((prev) => ({
      ...prev,
      statusId: autoCompleteData?.status?.defaultValue ?? null,
      responsiblePersonId: user.userId,
    }));
  }, [open, autoCompleteData?.status, user.userId]);

  const validationSchema = Yup.object().shape({
    projectName: Yup.string().required(),
    projectTypeId: Yup.string().nullable(),
    projectTypeStatusId: Yup.string().when('projectTypeId', {
      is: (projectTypeId) => !!projectTypeId && projectTypeStatusFieldShowed,
      then: (schema) => schema.required(),
      otherwise: (schema) => schema.nullable(),
    }),
    statusId: Yup.string().nullable(),
    responsiblePersonId: Yup.string().nullable(),
    companyId: Yup.string().nullable(),
    companyContactId: Yup.string().nullable(),
    companyContactRole: Yup.string().nullable(),
    companyContactPhoneNumber: Yup.string().nullable(),
    companyContactEmail: Yup.string().nullable(),
    companyLocationId: Yup.string().nullable(),
  });

  const handleCreate = (values, { resetForm }) => {
    const insertObject = {
      ...cleanValues(values),
      projectStartDate: dayjs(values.projectStartDate).startOf('day').add(12, 'hours').toISOString(),
      projectEndDate: dayjs(values.projectEndDate).startOf('day').add(12, 'hours').toISOString(),
      createdBy: user.userId,
      userId: user.userId,
    };

    const formData = new FormData();
    formData.append('data', JSON.stringify(insertObject));

    projectService
      .createProject(formData)
      .then((data) => {
        addNotification({
          content: 'Sikeres mentés!',
          type: NotificationType.SUCCESS,
        });
        resetForm();
        handleClose();
        setSelectedProjectId(data.projectId);

        const newProjectForAutocomplete = {
          projectId: data.projectId,
          projectName: data.projectName,
          orderNumber: data.orderNumber,
          active: data.active,
        };
        setAutoCompleteData((prev) => ({
          ...prev,
          project: [...prev.project, newProjectForAutocomplete],
        }));
      })
      .finally(() => {
        //@TODO loader
      });
  };

  return (
    <DialogUI
      open={open}
      onClose={handleClose}
      headerContent="Új adatlap"
      closeBtn
      sx={{
        '& .MuiDialog-container': {
          '& .MuiPaper-root': {
            width: '100%',
            maxWidth: '1000px',
            mx: 1,
          },
        },
        '& .MuiDialogContent-root': {
          p: 1.5,
        },
      }}
    >
      <Box mt={2}>
        <Formik
          initialValues={initValues}
          validationSchema={validationSchema}
          validateOnChange={false}
          onSubmit={handleCreate}
          enableReinitialize
        >
          {({}) => (
            <Form>
              <CreateWorkspaceDialogFields
                autoCompleteOptions={autoCompleteOptions}
                autoCompleteData={autoCompleteData}
              />

              <Box textAlign="center" mt={1.5}>
                <Button type="submit" variant="contained">
                  Mentés
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      </Box>
    </DialogUI>
  );
};

export default CreateWorkspaceDialog;
